import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { ENQUIRY_TYPES, ENQUIRY_STATUS } from "@/lib/format";
import { Search, Phone, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLOR = {
  Buy: "#30d158", Sell: "#2997ff", "Trade-in": "#bf5af2", Repair: "#ff9f0a",
  Warranty: "#64d2ff", Business: "#ff375f", Other: "#98989d",
};
const STATUS_COLOR = {
  New: "#2997ff", Replied: "#30d158", "Visited store": "#bf5af2", Won: "#30d158", Lost: "#ff453a",
};

export default function Enquiries() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [due, setDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("All");
  const [status, setStatus] = useState(params.get("due") ? "All" : (params.get("status") || "All"));
  const onlyDue = !!params.get("due");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, dueRes] = await Promise.all([
        api.get("/enquiries", { params: { search: search || undefined, type, status } }),
        api.get("/enquiries/followups/today"),
      ]);
      setItems(onlyDue ? dueRes.data : data);
      setDue(dueRes.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [search, type, status, onlyDue]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const sel = "h-10 rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]";

  return (
    <div className="mf-fade-in">
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Enquiries</h1>
      <p className="mb-4 text-sm text-[#98989d]">{items.length} total</p>

      {due.length > 0 && (
        <div data-testid="followups-due-banner" className="mb-4 rounded-2xl border border-[#ff9f0a]/30 bg-[#ff9f0a]/10 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#ff9f0a]">
            <CalendarClock size={16} /> Follow-ups due today ({due.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {due.map((d) => (
              <button key={d.id} onClick={() => navigate(`/enquiries/${d.id}`)} className="rounded-full border border-[#ff9f0a]/30 bg-black/40 px-3 py-1 text-xs font-medium">
                {d.name} · {d.type}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98989d]" />
          <input data-testid="search-enquiries-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, phone, email, code…" className="h-11 w-full rounded-xl border border-white/10 bg-[#121214] pl-9 pr-3 text-sm outline-none focus:border-[#2997ff]" />
        </div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <select data-testid="filter-enquiry-type" value={type} onChange={(e) => setType(e.target.value)} className={sel}>
          {["All", ...ENQUIRY_TYPES].map((x) => <option key={x}>{x}</option>)}
        </select>
        <select data-testid="filter-enquiry-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
          {["All", ...ENQUIRY_STATUS].map((x) => <option key={x}>{x}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2997ff]" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-[#98989d]">No enquiries yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((e) => (
            <button key={e.id} data-testid={`enquiry-row-${e.id}`} onClick={() => navigate(`/enquiries/${e.id}`)} className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-[#121214] p-4 text-left transition-transform active:scale-[0.99]">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">{e.name}</span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: TYPE_COLOR[e.type], background: `${TYPE_COLOR[e.type]}22` }}>{e.type}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-[#98989d]">
                  <Phone size={11} /> {e.phone}{e.stock_code ? ` · ${e.stock_code}` : ""}
                </div>
                {e.message && <div className="mt-1 truncate text-xs text-[#98989d]">{e.message}</div>}
              </div>
              <span className="ml-3 shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold" style={{ color: STATUS_COLOR[e.status], background: `${STATUS_COLOR[e.status]}22` }}>{e.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
